
// * ========================================================================
// * Summary chart
// * ========================================================================

import CovidChart from './covid-chart.js';
import moment from 'moment';
import storage from './../storage';

const medicalSummary = (function() {
    let thisChart;
    let chartData = null;
    const firstDates = ['',moment('2020-03-01'),moment('2020-07-01'),moment('2021-07-01'),moment('2022-07-01')];
    let firstDate = firstDates[0];

    /**
     *
     * Create new chart
     *
     * @param {Object} data - covid data
     * @param {Object} options - chart options
     * @param {Array} selected - selected datasets
     *
     * @returns {Object} - chart
     */
    function createChartData(data, options, selected) {

        firstDate = options.wave && typeof firstDates[options.wave] !== 'undefined' ? firstDates[options.wave] : '';
        if (!firstDate) {
            firstDate = options.firstDate; // User selected first date
        }

        const datasets = [{
            label: 'Sairaalassa yhteensä 7 vrk mediaani',
            id: 'hospitalized',
            borderColor: '#0293d6',
            labelColor: '#0293d6',
            borderWidth: 3,
            fill: false,
            selected: selected.indexOf('hospitalized') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            order: 9,
            stack: 'nope'
        },
        {
            label: 'Sairaalassa koronan kanssa 7 vrk mediaani',
            id: 'with_covid',
            borderColor: 'rgb(2, 147, 214, 0.01)',
            backgroundColor: 'rgb(0, 174, 255, 0.15)',
            labelColor: '#c9e9ff',
            borderWidth: 3,
            fill: 2,
            selected: selected.indexOf('with_covid') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            order: 8,
            stack: 'Stack 1'
        },
        {
            label: 'Sairaalassa koronan takia 7 vrk mediaani',
            id: 'due_covid',
            borderColor: 'rgb(2, 147, 214, 0.1)',
            backgroundColor: 'rgb(0, 156, 255, 0.35)', //'rgb(2, 147, 214, 0.3)',
            labelColor: '#60c1ff',
            borderWidth: 1,
            fill: 'origin',
            selected: selected.indexOf('due_covid') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            order: 7,
            stack: 'Stack 1'
        },
        {
            label: 'Kuolleet yhteensä / 21 vrk ka.',
            id: 'dead',
            borderColor: '#444444',
            labelColor: '#000',
            borderWidth: 3,
            fill: false,
            selected: selected.indexOf('dead') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            order: 3,
            stack: 'dead'
        },{
            label: 'Kuolemaan myötävaikuttanut / 21 vrk ka.',
            id: 'dead-with',
            borderColor: 'rgb(0, 0, 0, 0.01)',
            backgroundColor: 'rgb(0, 0, 0, 0.2)',
            labelColor: '#666',
            borderWidth: 3,
            fill: 5,
            selected: selected.indexOf('dead-with') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            order: 1,
            stack: 'Stack 2'
        },
        {
            label: 'Pääkuolinsyy / 21 vrk ka.',
            id: 'dead-due',
            borderColor: 'rgb(0, 0, 0, 0.01)',
            backgroundColor: 'rgb(0, 0, 0, 0.4)',
            labelColor: '#444',
            borderWidth: 3,
            fill: 'origin',
            selected: selected.indexOf('dead-due') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            order: 0,
            stack: 'Stack 2'
        },{
            label: 'Ajallisesti liittyvät kuolemat / 21 vrk ka.',
            id: 'dead-time',
            borderColor: '#e80a9a',
            labelColor: '#e80a9a',
            borderWidth: 3,
            fill: false,
            selected: selected.indexOf('dead-time') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            order: 2,
            stack: 'dead-time',
            borderDash: [6,6]
        },{
            label: 'Alkaneet sairaanhoitojaksot yht.',
            id: 'hospitalized-start',
            borderColor: '#ff6e04',
            labelColor: '#ff6e04',
            borderWidth: 3,
            fill: false,
            selected: selected.indexOf('hospitalized-start') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            order: 6,
            stack: 'nope2'
        },{
            label: 'Alkaneet sairaanhoitojaksot koronan kanssa',
            id: 'hospitalized-with-start',
            borderColor: 'rgb(255, 110, 4, 0.01)',
            backgroundColor: 'rgb(255, 110, 4, 0.3)',
            labelColor: '#ffc092',
            borderWidth: 3,
            fill: true,
            selected: selected.indexOf('hospitalized-with-start') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            order: 5,
            stack: 'stack-start',
        },{
            label: 'Alkaneet sairaanhoitojaksot koronan takia',
            id: 'hospitalized-for-start',
            borderColor: 'rgb(255, 110, 4, 0.01)',
            backgroundColor: 'rgb(255, 110, 4, 0.4)',
            labelColor: '#ff9242',
            borderWidth: 3,
            fill: true,
            selected: selected.indexOf('hospitalized-for-start') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            order: 4,
            stack: 'stack-start',
        }];

        const dayIdxMap = {};
        const summaryData = [];

        // Hospitalized
        let dayIdx = 0;
        for (let idx = 0; idx < data.hospital.weeks.length; idx++) {
            let day = data.hospital.weeks[idx];
            const date = moment(day.date);

            if (date >= firstDate) {
                summaryData.push({
                    dateString: day.date,
                    date: date,
                    hospitalized: day.hospitalized,
                    for: day.forCovidPrimary + day.forCovidSpecial,
                    with: day.withCovidPrimary + day.withCovidSpecial,
                    start: day.start,
                    startFor: day.startForCovid,
                    startWith: day.startWithCovid,
                });

                dayIdxMap[day.date] = dayIdx;
                dayIdx++;
            }
        }

        // Dead
        const deadDelay = moment().add(-21,'days');
        for (let deIdx = 0; deIdx < data.dead.days.length; deIdx++) {
            let deadDay = data.dead.days[deIdx];
            const dateObj = moment(deadDay.date);
            if (dateObj > deadDelay) {
                // no point of showing these because of delay
                continue;
            }

            let dIdx = typeof dayIdxMap[deadDay.date] !== 'undefined' ? dayIdxMap[deadDay.date] : false;
            if (typeof dIdx === 'number') {
                // Get past 3 week values
                let deathFirstDay = deIdx - 21;
                deathFirstDay = deathFirstDay < 0 ? 0 : deathFirstDay;

                let timeRelatedDeathsInTimePeriod = 0;
                let deathsInTimePeriod = 0;
                let leadingDeathsInTimePeriod = 0;
                let contributingDeathsInTimePeriod = 0;
                for (let x2 = deathFirstDay; x2 < deIdx; x2++) {
                    let prevDeaths = data.dead.days[x2].count && data.dead.days[x2].count > 0 ? data.dead.days[x2].count : 0;
                    deathsInTimePeriod += (0 + prevDeaths);
                    // leading
                    let prevDeathsLeading = data.dead.days[x2].leading && data.dead.days[x2].leading > 0 ? data.dead.days[x2].leading : 0;
                    leadingDeathsInTimePeriod += (0 + prevDeathsLeading);
                    // contributing
                    let prevDeathsContributing = data.dead.days[x2].contributing && data.dead.days[x2].contributing > 0 ? data.dead.days[x2].contributing : 0;
                    contributingDeathsInTimePeriod += (0 + prevDeathsContributing);

                    let prevTimeRelatedDeaths = data.dead.days[x2].timeRelated && data.dead.days[x2].timeRelated > 0 ? data.dead.days[x2].timeRelated : 0;
                    timeRelatedDeathsInTimePeriod += (0 + prevTimeRelatedDeaths);
                }

                summaryData[dIdx].timeRelatedDeaths = timeRelatedDeathsInTimePeriod;
                summaryData[dIdx].deaths = deathsInTimePeriod;
                summaryData[dIdx].leadingDeaths = leadingDeathsInTimePeriod;
                summaryData[dIdx].contributingDeaths = contributingDeathsInTimePeriod;
            }
        }

        // Fill in missing day data
        let previous = {
            /*hospitalized: 0,
            deaths: 0*/
        };

        let keys = ['hospitalized','deaths'];
        let lastValueIdx = {
            hospitalized: null,
            deaths: null
        }

        for (const [i, summaryDay] of summaryData.entries()) {
            for (let key of keys) {
                // Fill only after first found value
                if (typeof summaryDay[key] === 'undefined') {
                    // Only if previous is set and is not last days
                    if (typeof previous[key] !== 'undefined') {
                        summaryDay[key] = previous[key];

                        // Set zero new deaths for today
                        if (key === 'deaths') {
                            summaryDay[key] = 0;
                        }
                    }
                } else {
                    previous[key] = summaryDay[key];
                    lastValueIdx[key] = i;
                }
            }
        }

        // Add to datasets
        for (const [i, day] of summaryData.entries()) {
            if (lastValueIdx.hospitalized && lastValueIdx.hospitalized >= i) {
                datasets[0].data.push({
                    x: day.date,
                    y: day.hospitalized
                });
                datasets[1].data.push({
                    x: day.date,
                    y: day.with
                });
                datasets[2].data.push({
                    x: day.date,
                    y: day.for
                });
                datasets[7].data.push({
                    x: day.date,
                    y: day.start
                });
                datasets[8].data.push({
                    x: day.date,
                    y: day.startWith
                });
                datasets[9].data.push({
                    x: day.date,
                    y: day.startFor
                });
            }
            if (lastValueIdx.deaths && lastValueIdx.deaths >= i) {
                datasets[3].data.push({
                    x: day.date,
                    y: day.deaths,
                    deaths: day.deaths
                });
                datasets[4].data.push({
                    x: day.date,
                    y: day.contributingDeaths,
                    deaths: day.contributingDeaths
                });
                datasets[5].data.push({
                    x: day.date,
                    y: day.leadingDeaths,
                    deaths: day.leadingDeaths
                });
                datasets[6].data.push({
                    x: day.date,
                    y: day.timeRelatedDeaths,
                    deaths: day.timeRelatedDeaths
                });
            }
        }

        // Create line segments
        const lag = 4; // weeks

        const hospitalDataLagIdx = datasets[0].data.length - lag;
        const dashedHospital = function (ctx, value) {
            if (ctx.p0DataIndex > hospitalDataLagIdx) {
                return value;
            }
            return undefined;
        }

        datasets[0].segment = {
            borderColor: ctx => dashedHospital(ctx, 'rgb(2 147 214 / 50%)'),
            borderDash: ctx => dashedHospital(ctx, [6, 6]),
        };
        /*datasets[1].segment = {
            borderColor: ctx => dashedHospital(ctx, 'rgb(2 147 214 / 50%)'),
            borderDash: ctx => dashedHospital(ctx, [6, 6]),
        };*/

        datasets[7].segment = {
            borderColor: ctx => dashedHospital(ctx, 'rgb(255 110 4 / 50%)'),
            borderDash: ctx => dashedHospital(ctx, [6, 6]),
        };

        // Deaths
        const deadDataLagIdx = datasets[3].data.length - lag;
        const dashedDead = function (ctx, value) {
            if (ctx.p0DataIndex > deadDataLagIdx) {
                return value;
            }
            return undefined;
        }

        datasets[3].segment = {
            borderColor: ctx => dashedDead(ctx, 'rgb(102 102 102 / 50%)'),
            borderDash: ctx => dashedDead(ctx, [6, 6]),
        };

        return datasets;
    }

    function getSelected() {
        // Initially selected
        let selected = null; //storage.getData('medical-summary-chart_multiselect');
        if (!selected || !Array.isArray(selected)) {
            selected = ['hospitalized','dead','dead-time','hospitalized-start'];
        }

        return selected;
    }

    /**
     * Initialize
     *
     * @param {Object} data - covid data
     */
    function init(data) {
        chartData = data;

        const selected = getSelected();
        const datasets = createChartData(data, window.config, selected);
        thisChart = new CovidChart('medical-summary-chart', {
            datasets,
            chartOptions: {
                options: {
                    plugins: {
                        filler: {
                            propagate: true
                        }
                    },
                    legend: {
                        display: true,
                        labels: {
                            boxWidth: 12
                        }
                    },
                    tooltips: {
                        position: 'nearest',
                        intersect: false,
                        bodySpacing: 5,
                    },
                    scales: {
                        x: {
                            type: 'time',
                            display: true,
                            time: {
                                unit: 'day',
                                displayFormats: {
                                    day: 'D.M.YY',
                                }
                            },
                            ticks: {
                                major: {
                                    fontStyle: 'bold',
                                    fontColor: '#FF0000'
                                }
                            },
                            grid: {
                                display: false
                            }
                        },
                        y: {
                            stacked: true,
                            min: 0,
                            type: 'linear',
                            position: 'left'
                        }
                    }
                }
            },
            delayAnnotation: true,
            config: window.config,
            tooltips: {
                elementId: 'medical-summary-chart-tooltip'
            },
            multiselect: {
                requireCount: false,
                valueType: 'string',
                idField: 'id',
                colorField: 'labelColor',
                inputName: 'medical-summary-multiselect',
                selectElemId: 'medical-summary-multiselect',
                colorElemId: 'medical-summary-multiselect-colors',
                dropdownId: 'medical-summary-multiselect-dropdown',
                selectAllElemId: 'medical-summary-multiselect-select-all',
                clearAllElemId: 'medical-summary-multiselect-clear-all',
                countElemId: 'medical-summary-multiselect-count'
            }
        });
    }

    function reload(data) {
        const selected = getSelected();
        const datasets = createChartData(data, window.config, selected);
        thisChart.updateDatasets(datasets);
    }

    return {
        init,
        reload
    }

})();

export default medicalSummary;
